import MobileAppBar from '@/components/molecules/MobileAppBar';
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import binIcon from '@/assets/icons/bin.svg';
import budgetIcon from '@/assets/icons/budget.svg';
import editIcon from '@/assets/icons/edit.svg';
import percentageIcon from '@/assets/icons/percentage.svg';
import euroIcon from '@/assets/icons/euro.svg';
import vaultIcon from '@/assets/icons/vault.svg';
import plusIcon from '@/assets/icons/plus.svg';
import buildingIcon from '@/assets/icons/building.svg';
import noInvoiceIcon from '@/assets/icons/no-invoice.svg';
import departmentACIcon from '@/assets/icons/department-acs.svg';
import productsACIcon from '@/assets/icons/product-and-services.svg';
import projectsACIcon from '@/assets/icons/project-acs.svg';
import contractsACIcon from '@/assets/icons/contracts-acs.svg';
import expenseIcon from '@/assets/icons/expense.svg';
import revenueIcon from '@/assets/icons/revenue.svg';
import { ReactComponent as InvoiceUploadIcon } from '@/assets/icons/upload-invoice.svg';
import invoiceIcon from '@/assets/icons/invoice.svg';
import workActivityIcon from '@/assets/icons/work-activity.svg';
import infoIcon from '@/assets/icons/info.svg';
import noWorkActivityIcon from '@/assets/icons/no-work-activity.svg';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChartConfig } from '@/components/ui/chart';
import PieGraph from '@/components/organisms/graphs/PieGraph';
import BudgetGeneralInfoAccordion from '@/components/molecules/BudgetGeneralInfoAccordion';
import OverviewList, { ListItem } from '@/components/organisms/OverviewList';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { useDeviceType } from '@/hooks/useDeviceType';
import BudgetInfoCard from '@/components/molecules/BudgetInfoCard';
import { budgetService } from '@/services/budget.service';
import { formatDate, getCurrencyInfo } from '@/utils/helpers';
import { formatBudgetCalculationAmount } from '@/utils/budgetCalculations';
import InfoModal from '@/components/molecules/InfoModal';
import deleteBudgetIcon from '@/assets/icons/delete-budget.svg';
import toast from 'react-hot-toast';
import { bankAccountService } from '@/services/bank-accounts.service';

import toggleACOffIcon from '@/assets/icons/ac-toggle-off.svg';
import toggleECOffIcon from '@/assets/icons/ec-toggle-off.svg';

// import { useParams } from 'react-router-dom';
import { PAGE } from '@/routes';
import { accountingCenterService } from '@/services/accounting-centers.service';
import AccountCenterToggleItem from '@/components/molecules/AccountCenterToggleItem';
import ToggleItem from '@/components/molecules/ToggleItem';
import { expenseRevenueService } from '@/services/expense-category.service';
import { revenueCategoryService } from '@/services/revenue-category.service';
import ExplanatoryModal from '@/components/molecules/ExplanatoryModal';

import budgetExpanatoryImg from '@/assets/images/budget-explanatory-1.png';
import budgetExpanatoryImg2 from '@/assets/images/budget-explanatory-2.png';
import budgetExpanatoryImg3 from '@/assets/images/budget-explanatory-3.png';
import budgetExpanatoryImg4 from '@/assets/images/budget-explanatory-4.png';
import budgetExpanatoryImg5 from '@/assets/images/budget-explanatory-5.png';
import budgetExpanatoryImg6 from '@/assets/images/budget-explanatory-6.png';
import { invoiceService } from '@/services/invoice.service';
import { workActivityService } from '@/services/work-activity.service';
import InvoicesTable from '@/components/molecules/invoice/InvoicesTable';
import WorkActivityTable from '@/components/molecules/workActivity/WorkActivityTable';
import TabPills from '@/components/molecules/TabPills';
import { Budget, BudgetMemberEntry } from '@/services/types/budgets.types';
import defaultUserAvatar from '@/assets/images/default-user.png';
import { Invoice, Commitment as CommitmentType } from '@/services/types/invoice.types';
import { WorkActivity, WorkActivityStatus } from '@/services/types/work-activity.types';
import CommitmentCalendar, { Commitment as CalendarCommitment } from '@/pages/Commitment/CommitmentCalendar';
import CommitmentList from '@/pages/Commitment/CommitmentList';
import { format } from 'date-fns';
import { calendarMonthToApiDate, commitmentDateToCalendarKey } from '@/utils/commitmentCalendarDate';
import { useBudgetPermissions } from '@/hooks/useBudgetPermissions';

const WORK_ACTIVITY_FILTER_OPTIONS = ['All', 'Approved', 'Pending', 'Rejected'] as const;

const WORK_ACTIVITY_STATUS_BY_FILTER: Record<
  (typeof WORK_ACTIVITY_FILTER_OPTIONS)[number],
  WorkActivityStatus | undefined
> = {
  All: undefined,
  Approved: 'APPROVED',
  Pending: 'PENDING',
  Rejected: 'REJECTED'
};

const ACCOUNTING_CENTERS = [
  {
    title: 'Department Account Center',
    value: 'DEPARTMENT_ACCOUNTING',
    icon: departmentACIcon
  },
  {
    title: 'Products & Services Accounting Center',
    value: 'PRODUCTS_AND_SERVICES',
    icon: productsACIcon
  },
  {
    title: 'Projects Accounting Center',
    value: 'PROJECTS',
    icon: projectsACIcon
  },
  {
    title: 'Contract Accounting Centers',
    value: 'CONTRACTS',
    icon: contractsACIcon
  }
];

const EXPENSE_ITEM = {
  title: 'Expense Categories',
  value: 'EXPENSE',
  icon: expenseIcon
};

const REVENUE_ITEM = {
  title: 'Revenue Categories',
  value: 'REVENUE',
  icon: revenueIcon
};

function commitmentCalendarRow(c: CommitmentType): CalendarCommitment {
  return {
    id: c.id,
    title: c.title,
    budgetName: c.budgetName,
    date: new Date(c.date)
  };
}

const steps = [
  {
    title: 'What Is a Budget?',
    message:
      'A budget serves as the foundation for managing your cashflow, allowing you to allocate income and expenses efficiently.',
    img: budgetExpanatoryImg
  },
  {
    title: 'Understanding BMEV and BTRT',
    message:
      'BMEV is the maximum spending limit set for your budget, ensuring expenses remain within control. While BTRT is the revenue target you aim to achieve, guiding your financial planning.',
    img: budgetExpanatoryImg2
  },
  {
    title: 'Bank Accounts on Your Budget',
    message:
      'Each budget links directly to bank accounts, where initial balances are assigned to fund expenses and receive revenue.',
    img: budgetExpanatoryImg3
  },
  {
    title: 'Organizing with Accounting Centers',
    message:
      'Accounting Centers (ACs) categorize your financial activities, dividing them into departments. Each AC aligns with the budget’s BMEV and BTRT.',
    img: budgetExpanatoryImg4
  },
  {
    title: 'Tracking Invoices in Your Budget',
    message:
      'Invoices represent your expenses or revenues. They are allocated to categories such as bank accounts, ACs, and EC/RCs for organized tracking.',
    img: budgetExpanatoryImg5
  },
  {
    title: 'Commitments: Plan Before You Pay',
    message:
      'Commitments are unpaid invoices added to the budget. You’ll receive reminders about their payment dates to ensure timely management.',
    img: budgetExpanatoryImg6
  }
];

const DetailPage = () => {
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [commitments, setCommitments] = useState<CommitmentType[]>([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [commitmentsLoading, setCommitmentsLoading] = useState(false);
  const { isMobileView } = useDeviceType();
  const { budgetId } = useParams();
  // const budgetId = id || '';
  const navigate = useNavigate();

  const [budgetFabSelectKey, setBudgetFabSelectKey] = useState(0);

  const handleBudgetFabAction = (action: string) => {
    if (action === 'upload-invoice') {
      navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
        state: { from: 'budget', budgetId }
      });
    }

    if (action === 'log-work-activity') {
      navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/create`, {
        state: { from: 'budget', budgetId }
      });
    }

    setBudgetFabSelectKey((key) => key + 1);
  };

  const [budget, setBudget] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [graphViewMode, setGraphViewMode] = useState('percentage');
  const [bankAccounts, setBankAccounts] = useState<any>([]);

  const [accountingCenters, setAccountingCenters] = useState<any>([]);
  const [checked, setChecked] = useState(false);
  const [loadingAccountingCenters, setLoadingAccountingCenters] = useState(false);

  const [accountingCentersToggle, setAccountingCentersToggle] = useState<any>({
    DEPARTMENT_ACCOUNTING: false,
    PRODUCTS_AND_SERVICES: false,
    PROJECTS: false,
    CONTRACTS: false
  });

  const [deactivateACConfirmModal, setDeactivateACConfirmModal] = useState(false);
  const [selectedAC, setSelectedAC] = useState<any>(null);

  const [expenseToggle, setExpenseToggle] = useState<boolean>(false);
  const [revenueToggle, setRevenueToggle] = useState<boolean>(false);
  const [loadingExpense, setLoadingExpense] = useState<boolean>(false);
  const [loadingRevenue, setLoadingRevenue] = useState<boolean>(false);

  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const [deactivateExpenseConfirmModal, setDeactivateExpenseConfirmModal] = useState(false);
  const [deactivateRevenueConfirmModal, setDeactivateRevenueConfirmModal] = useState(false);

  const [invoices, setInvoices] = useState<any>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [activeInvoiceTab, setActiveInvoiceTab] = useState('All');
  const [workActivities, setWorkActivities] = useState<WorkActivity[]>([]);
  const [totalWorkActivityCount, setTotalWorkActivityCount] = useState(0);
  const [loadingWorkActivities, setLoadingWorkActivities] = useState(false);
  const [activeWorkActivityTab, setActiveWorkActivityTab] =
    useState<(typeof WORK_ACTIVITY_FILTER_OPTIONS)[number]>('All');

  const budgetCommitments = useMemo(() => {
    if (!budget?.name) return [];
    const name = String(budget.name).trim().toLowerCase();
    return commitments.filter((c) => c.budgetName?.trim().toLowerCase() === name);
  }, [commitments, budget?.name]);

  const selectedDateCommitments = useMemo(() => {
    if (!date) return [];
    const key = format(date, 'yyyy-MM-dd');
    return budgetCommitments.filter((c) => commitmentDateToCalendarKey(c.date) === key).map(commitmentCalendarRow);
  }, [date, budgetCommitments]);

  const budgetMembers = useMemo<BudgetMemberEntry[]>(() => budget?.members ?? [], [budget?.members]);

  const { can } = useBudgetPermissions({
    budgetId,
    members: budgetMembers
  });

  const canCreateInvoice = can('invoice:create');
  const canCreateWorkActivity = can('workActivity:create');
  const canCreateBankAccount = can('bankAccount:create');
  const canEditBudget = can('budget:edit');
  const canDeleteBudget = can('budget:delete');
  const canToggleAccountingCenterActivation = can('accountingCenter:activate');
  const canToggleExpenseCategoryActivation = can('expenseCategory:activate');
  const canToggleRevenueCategoryActivation = can('revenueCategory:activate');

  const visibleMemberAvatars = useMemo(() => budgetMembers.slice(0, 4), [budgetMembers]);

  const memberOverflowCount = useMemo(
    () => (budgetMembers.length > 4 ? budgetMembers.length - 4 : 0),
    [budgetMembers.length]
  );

  const getMemberAvatarSrc = (member: BudgetMemberEntry) => member.profile_pic_url || defaultUserAvatar;

  useEffect(() => {
    if (!budgetId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setCommitmentsLoading(true);
        if (!cancelled) setCommitments([]);
        const { data } = await invoiceService.getCommitments({
          date: calendarMonthToApiDate(calendarMonth),
          budget_id: budgetId || ''
        });
        if (!cancelled) setCommitments(data);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setCommitmentsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [calendarMonth, budgetId]);

  const handleCheckChange = (checked: boolean) => {
    setChecked(checked);
  };

  const fetchAccountingCenters = async () => {
    setLoadingAccountingCenters(true);
    try {
      const { data } = await accountingCenterService.getAccountingCenters(budgetId || '');
      setAccountingCenters(data);

      // Initialize toggle states based on data from API
      const newToggleState = { ...accountingCentersToggle };

      // Update toggle state based on fetched data
      data.forEach((center: any) => {
        if (center && center.type && center.is_default) {
          newToggleState[center.type] = center.is_default || false;
        }
      });

      setAccountingCentersToggle(newToggleState);
    } catch (err) {
      console.error('Error fetching accounting centers:', err);
    } finally {
      setLoadingAccountingCenters(false);
    }
  };

  // Fetch expense and revenue status
  const fetchExpenseRevenueStatus = async () => {
    if (!budgetId) return;

    try {
      // Fetch expense categories to check if expense is activated
      setLoadingExpense(true);
      const expenseResponse = await expenseRevenueService.getExpenseCategories(budgetId);
      setExpenseToggle(expenseResponse.data && expenseResponse.data.length > 0);
    } catch (error) {
      console.error('Error fetching expense status:', error);
      setExpenseToggle(false);
    } finally {
      setLoadingExpense(false);
    }

    try {
      // Fetch revenue categories to check if revenue is activated
      setLoadingRevenue(true);
      const revenueResponse = await revenueCategoryService.getRevenueCategories(budgetId);
      setRevenueToggle(revenueResponse.data && revenueResponse.data.length > 0);
    } catch (error) {
      console.error('Error fetching revenue status:', error);
      setRevenueToggle(false);
    } finally {
      setLoadingRevenue(false);
    }
  };

  const fetchBudgetDetails = async () => {
    if (!budgetId) {
      setError('Budget ID not found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching budget with ID:', budgetId);
      const { data } = await budgetService.getBudget(budgetId);

      setBudget(data);
    } catch (err) {
      console.error('Error fetching budget:', err);
      setError('Failed to load budget details');
    } finally {
      setLoading(false);
    }
  };

  // Update your fetchBankAccounts function to transform the data
  const fetchBankAccounts = async () => {
    if (budgetId) {
      try {
        const { data: bankAccountsData } = await bankAccountService.getBankAccounts(budgetId);

        // Transform the data into the expected format
        const formattedBankAccounts = bankAccountsData.map((account) => ({
          id: account._id,
          title: `${account.accountName}`,
          icon: vaultIcon,
          data: [
            {
              subtitle: 'Remaining balance',
              value: String(account.remainingBalance || 0)
            }
          ]
        }));

        setBankAccounts(formattedBankAccounts);
      } catch (error) {
        console.error('Error fetching bank accounts:', error);
        setBankAccounts([]);
      }
    }
  };

  const fetchBudgetInvoices = async () => {
    if (budgetId) {
      try {
        const { data: invoicesData } = await invoiceService.getInvoices({
          page: 1,
          limit: 12,
          budget_id: budgetId,
          invoice_category: activeInvoiceTab === 'All' ? undefined : activeInvoiceTab.toUpperCase(),
          type: undefined
        });
        const filteredInvoices =
          invoicesData.data && invoicesData.data.length > 0
            ? invoicesData.data.filter((invoice: any) => invoice.is_draft === false)
            : [];
        setInvoices(filteredInvoices || []);
      } catch (error) {
        console.error('Error fetching budget invoices:', error);
      } finally {
        setLoadingInvoices(false);
      }
    }
  };

  const fetchBudgetWorkActivities = async () => {
    if (!budgetId) {
      return;
    }

    try {
      setLoadingWorkActivities(true);
      const response = await workActivityService.getWorkActivities(budgetId, {
        page: 1,
        limit: 8,
        type: 'all'
      });
      setWorkActivities(Array.isArray(response.data) ? response.data : []);
      setTotalWorkActivityCount(response.totalCount ?? 0);
    } catch (error) {
      console.error('Error fetching budget work activities:', error);
      setWorkActivities([]);
      setTotalWorkActivityCount(0);
    } finally {
      setLoadingWorkActivities(false);
    }
  };

  const filteredWorkActivities = useMemo(() => {
    if (!Array.isArray(workActivities)) {
      return [];
    }

    const statusFilter = WORK_ACTIVITY_STATUS_BY_FILTER[activeWorkActivityTab];
    const filtered = statusFilter
      ? workActivities.filter((activity) => activity.status === statusFilter)
      : workActivities;

    return filtered.slice(0, 7);
  }, [activeWorkActivityTab, workActivities]);

  const pendingWorkActivityCount = useMemo(() => {
    if (!Array.isArray(workActivities)) {
      return 0;
    }

    return workActivities.filter((activity) => activity.status === 'PENDING').length;
  }, [workActivities]);

  const workActivityTabBadges = useMemo(
    () => (pendingWorkActivityCount > 0 ? { Pending: pendingWorkActivityCount } : undefined),
    [pendingWorkActivityCount]
  );

  useEffect(() => {
    fetchBudgetDetails();
    fetchBankAccounts();
    fetchAccountingCenters();
    fetchExpenseRevenueStatus();
  }, [budgetId]);

  useEffect(() => {
    fetchBudgetInvoices();
  }, [activeInvoiceTab, budgetId]);

  useEffect(() => {
    fetchBudgetWorkActivities();
  }, [budgetId]);

  const handleDeleteClick = () => {
    setDeleteConfirmModal(true);
  };

  const handleDeleteBudget = async () => {
    try {
      await budgetService.deleteBudget(budgetId!);

      toast.success('Budget deleted successfully');
      navigate('/budgets');
    } catch (err) {
      console.error('Error deleting budget:', err);
      alert('Failed to delete budget');
    }
  };

  const handleAddBankClick = () => {
    if (budgetId) {
      navigate(`/budgets/${budgetId}/bank-accounts/add`);
    }
  };

  const handleSeeAllBankClick = () => {
    if (budgetId) {
      navigate(`/budgets/${budgetId}/bank-accounts`);
    }
  };

  const handleBankAccountClick = (bankAccountId: string) => {
    if (budgetId) {
      navigate(`/budgets/${budgetId}/bank-accounts/${bankAccountId}`);
    }
  };

  const handleActivateAC = async (value: string) => {
    try {
      const { data: responseData } = await accountingCenterService.activateACs(budgetId || '', { type: value });
      toast.success(`${responseData?.name || 'Accounting center'} activated successfully`);

      // Update the toggle state on success
      setAccountingCentersToggle({
        ...accountingCentersToggle,
        [value]: true
      });

      // Refresh accounting centers data
      fetchAccountingCenters();

      return true;
    } catch (error) {
      console.error('Error in handleActivateAC:', error);
      toast.error('Failed to activate accounting center');
      return false;
    }
  };

  const handleDeactivateAC = async (value: string) => {
    setSelectedAC(value);
    setDeactivateACConfirmModal(true);
    return false; // Return false to prevent immediate toggle change
  };

  const confirmDeactivateAC = async () => {
    if (!selectedAC) return;

    try {
      // Call the API to deactivate the accounting center
      await accountingCenterService.deactivateAC(budgetId || '', { type: selectedAC });

      toast.success('Accounting center deactivated successfully');

      // Update the toggle state on success
      setAccountingCentersToggle({
        ...accountingCentersToggle,
        [selectedAC]: false
      });

      // Close the modal and reset selectedAC
      setDeactivateACConfirmModal(false);
      setSelectedAC(null);
    } catch (error) {
      console.error('Error in confirmDeactivateAC:', error);
      toast.error('Failed to deactivate accounting center');
    }
  };

  const handleExpenseRevenueToggle = async (value: string, checked: boolean) => {
    try {
      // For now, just update the toggle state
      // API integration can be added later
      setExpenseToggle(checked);
      setRevenueToggle(checked);

      const itemName = value === 'EXPENSE' ? 'Expense' : 'Revenue';
      const action = checked ? 'activated' : 'deactivated';
      toast.success(`${itemName} ${action} successfully`);

      return true;
    } catch (error) {
      console.error('Error in handleExpenseRevenueToggle:', error);
      toast.error('Failed to update expense/revenue setting');
      return false;
    }
  };

  // Calculate chart data based on budget values
  const calculateChartData = () => {
    if (!budget) {
      return {
        btevChartData: [
          { key: 'remaining', value: 100, fill: '#32ADE6' },
          { key: 'bmevSpent', value: 0, fill: '#FF2D55' },
          { key: 'bhovSpent', value: 0, fill: '#FF9500' }
        ],
        btevRemainingPercent: 100,
        btevRemainingAmount: 0,
        btrtChartData: [{ key: 'target', value: 100, fill: '#32ADE6' }]
      };
    }

    const bmevUsed = budget.bmev_used ?? 0;
    const bmevRemaining = budget.bmev ?? 0;
    const bhovTotal = budget.bhov ?? 0;
    const bhovUsed = budget.bhov_used ?? 0;
    const bhovRemaining = Math.max(0, bhovTotal - bhovUsed);
    const btevTotal = budget.btev ?? bmevRemaining + bmevUsed + bhovTotal;
    const btevRemainingAmount = bmevRemaining + bhovRemaining;

    let btevData = [];
    let remainingBtevPercent = 100;

    if (btevTotal > 0) {
      const remainingPercent = Math.round((btevRemainingAmount / btevTotal) * 100);
      const bmevSpentPercent = Math.round((bmevUsed / btevTotal) * 100);
      const bhovSpentPercent = Math.round((bhovUsed / btevTotal) * 100);

      btevData = [
        { key: 'remaining', value: remainingPercent, fill: '#32ADE6' },
        { key: 'bmevSpent', value: bmevSpentPercent, fill: '#FF2D55' },
        { key: 'bhovSpent', value: bhovSpentPercent, fill: '#FF9500' }
      ];

      remainingBtevPercent = remainingPercent;
    } else {
      btevData = [
        { key: 'remaining', value: 100, fill: '#32ADE6' },
        { key: 'bmevSpent', value: 0, fill: '#FF2D55' },
        { key: 'bhovSpent', value: 0, fill: '#FF9500' }
      ];
    }

    // Calculate percentages for BTRT (Budget Total Revenue Target)
    let btrtData = [];
    if (budget.btrt !== undefined) {
      const btrtTotal = budget.btrt || 0;
      // Using btrt_achieved as the API response shows this field, not btrt_used
      const btrtAchieved = budget.btrt_achieved || 0;

      // Only calculate percentages if there's a target
      if (btrtTotal > 0) {
        const btrtAchievedPercent = Math.round((btrtAchieved / btrtTotal) * 100);

        // Add target (always 100%)
        btrtData.push({ key: 'target', value: 100 - btrtAchievedPercent, fill: '#32ADE6' });

        btrtData.push({ key: 'achieved', value: btrtAchievedPercent, fill: '#34C759' });
      } else {
        // Default value if btrt is 0
        btrtData.push({ key: 'target', value: 100, fill: '#32ADE6' });
      }
    } else {
      // Default value if btrt is not defined
      btrtData.push({ key: 'target', value: 100, fill: '#32ADE6' });
    }

    return {
      btevChartData: btevData,
      btevRemainingPercent: remainingBtevPercent,
      btevRemainingAmount,
      btrtChartData: btrtData
    };
  };

  const { btevChartData, btevRemainingPercent, btevRemainingAmount, btrtChartData } = calculateChartData();

  const btevChartConfig = {
    remaining: {
      label: 'Remaining Budget',
      color: '#32ADE6'
    },
    bmevSpent: {
      label: 'BMEV Spent',
      color: '#FF2D55'
    },
    bhovSpent: {
      label: 'BHOV Spent',
      color: '#FF9500'
    }
  } satisfies ChartConfig;

  const btrtChartConfig = {
    target: {
      label: 'Revenue Target',
      color: '#32ADE6'
    },
    achieved: {
      label: 'Achieved Revenue',
      color: '#34C759'
    }
  } satisfies ChartConfig;

  // Bank accounts data

  // Expense and revenues
  const expenseAndRevenues: ListItem[] = [];

  const handleActivateExpense = async () => {
    try {
      setLoadingExpense(true);
      await expenseRevenueService.activateExpenseCategories(budgetId || '', { type: 'EXPENSE_CATEGORY' });
      toast.success('Expense activated successfully');
      setExpenseToggle(true);
      return true;
    } catch (error) {
      console.error('Error activating expense:', error);
      toast.error('Failed to activate expense');
      return false;
    } finally {
      setLoadingExpense(false);
    }
  };

  const handleDeactivateExpense = async () => {
    setDeactivateExpenseConfirmModal(true);
    return false; // Return false to prevent immediate toggle change
  };

  const confirmDeactivateExpense = async () => {
    try {
      setLoadingExpense(true);
      await expenseRevenueService.deactivateExpenseCategory(budgetId || '', {});
      toast.success('Expense deactivated successfully');
      setExpenseToggle(false);
      setDeactivateExpenseConfirmModal(false);
    } catch (error) {
      console.error('Error deactivating expense:', error);
      toast.error('Failed to deactivate expense');
    } finally {
      setLoadingExpense(false);
    }
  };

  const handleActivateRevenue = async () => {
    try {
      setLoadingRevenue(true);
      await revenueCategoryService.activateRevenueCategories(budgetId || '', { type: 'REVENUE_CATEGORY' });
      toast.success('Revenue activated successfully');
      setRevenueToggle(true);
      return true;
    } catch (error) {
      console.error('Error activating revenue:', error);
      toast.error('Failed to activate revenue');
      return false;
    } finally {
      setLoadingRevenue(false);
    }
  };

  const handleDeactivateRevenue = async () => {
    setDeactivateRevenueConfirmModal(true);
    return false; // Return false to prevent immediate toggle change
  };

  const confirmDeactivateRevenue = async () => {
    try {
      setLoadingRevenue(true);
      await revenueCategoryService.deactivateRevenueCategory(budgetId || '', {});
      toast.success('Revenue deactivated successfully');
      setRevenueToggle(false);
      setDeactivateRevenueConfirmModal(false);
    } catch (error) {
      console.error('Error deactivating revenue:', error);
      toast.error('Failed to deactivate revenue');
    } finally {
      setLoadingRevenue(false);
    }
  };

  const handleInvoiceSelect = (invoice: Invoice & { budget?: Budget }) => {
    console.log('Invoice', invoice);
    if (budgetId) {
      navigate(`/budgets/${budgetId}/invoices/${invoice._id}`);
    }
  };

  const handleWorkActivitySelect = (workActivity: WorkActivity) => {
    if (budgetId) {
      navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/${workActivity._id}`);
    }
  };

  const handleCommitmentDateSelect = (d: Date) => {
    setDate(d);
  };

  const handleCreateBudgetCommitment = () => {
    navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
      state: {
        from: 'budget',
        budgetId,
        ...(date ? { issueDate: date.toISOString() } : {})
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p>Loading budget details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <>
      <MobileAppBar
        title="Budget Overview"
        rightTwoIcon={canDeleteBudget ? binIcon : undefined}
        rightIcon={infoIcon}
        onRightTwoIconClick={canDeleteBudget ? handleDeleteClick : undefined}
        onRightIconClick={() => {
          setCurrentStep(0);
          setIsOpen(true);
        }}
        rightIconClassName="w-[24px] h-[24px]"
      />
      <div className="p-4 ">
        {/* BUDGET INFORMATION - MOBILE */}
        <div className="flex items-center justify-between gap-5 mb-6 md:hidden">
          <div className="flex items-center gap-3">
            <img src={budgetIcon} alt="budget-icon" width={40} height={40} />
            <div>
              <h4 className="font-semibold text-base">{budget?.name || 'BUDGET NAME'}</h4>
              <p className="text-secondary text-sm font-medium">{budget?.type || 'Company'}</p>
            </div>
          </div>

          {canEditBudget && (
            <button type="button" onClick={() => navigate(`/budgets/edit/${budgetId}`)}>
              <img src={editIcon} alt="edit-icon" width={24} height={24} />
            </button>
          )}
        </div>

        <div className="xl:flex xl:gap-7">
          {/* BUDGET INFORMATION - WEB */}
          <BudgetInfoCard
            name={budget?.name}
            type={budget?.type}
            description={budget?.description}
            currency={budget?.currency}
            startDate={formatDate(budget?.start_date)}
            endDate={formatDate(budget?.end_date)}
            isAutoRenewal={budget?.is_auto_renewal}
            onEdit={canEditBudget ? () => navigate(`/budgets/edit/${budgetId}`) : undefined}
            onDelete={canDeleteBudget ? handleDeleteClick : undefined}
            onInfo={() => {
              setCurrentStep(0);
              setIsOpen(true);
            }}
            bmev={budget?.bmev + budget?.bmev_used}
            bmevThreshold={budget?.bmev_alert_threshold}
            btrt={budget?.btrt}
            btrtThreshold={budget?.btrt_alert_threshold}
            bhov={budget?.bhov ?? 0}
            btoh={budget?.btoh ?? 0}
            btev={budget?.btev ?? (budget?.bmev ?? 0) + (budget?.bmev_used ?? 0) + (budget?.bhov ?? 0)}
          />

          <div className="flex flex-col gap-6 flex-1 xl:w-[40%]">
            <div className="bg-lightPurple rounded-[16px] p-4 pb-2">
              <header className="flex items-center justify-between mb-3 md:mb-4">
                <p className="text-lightPurple-300 text-xs font-medium">Members</p>
                <Button
                  onClick={() => navigate(`/budgets/${budgetId}/members`)}
                  variant="text"
                  className="text-primary text-xs font-medium !py-0 !px-1 !h-fit"
                >
                  See All
                </Button>
              </header>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">
                  {budgetMembers.length} {budgetMembers.length === 1 ? 'Person' : 'Persons'}
                </p>
                <div className="flex items-center">
                  {visibleMemberAvatars.map((member, index) => (
                    <img
                      key={member._id}
                      src={getMemberAvatarSrc(member)}
                      alt={member.name}
                      className={`w-8 h-8 border-2 border-white rounded-full object-cover bg-lightGray ${index === 0 ? '' : '-ml-1.5'}`}
                      onError={(event) => {
                        event.currentTarget.src = defaultUserAvatar;
                      }}
                    />
                  ))}
                  {memberOverflowCount > 0 && (
                    <div className="w-8 h-8 rounded-full bg-primary -ml-1.5 text-xs font-medium text-white flex items-center justify-center">
                      +{memberOverflowCount}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* GRAPH OVERVIEW */}
            <div className="flex-1  md:bg-white md:p-[0.85rem] md:rounded-[16px]">
              <header className="w-full flex items-center justify-between mb-3 md:mb-4">
                <h3 className="text-base font-bold text-primary md:text-foreground">Graph Overview</h3>
                <Tabs defaultValue="percentage" value={graphViewMode} onValueChange={setGraphViewMode}>
                  <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                    <TabsTrigger value="percentage" className="group md:py-[0.3rem]">
                      <img
                        src={percentageIcon}
                        alt="percentage-icon"
                        width={13}
                        height={17}
                        className="group-data-[state=active]:brightness-[100]"
                      />
                    </TabsTrigger>
                    <TabsTrigger value="euro" className="group md:py-[0.3rem]">
                      <img
                        src={euroIcon}
                        alt="euro-icon"
                        width={12}
                        height={12}
                        className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                      />
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </header>
              <div className="flex-1 flex justify-between gap-3">
                <PieGraph
                  title="Remaining BTEV"
                  titleValue={(() => {
                    if (graphViewMode === 'percentage') {
                      return `${btevRemainingPercent}%`;
                    }

                    return formatBudgetCalculationAmount(btevRemainingAmount, budget?.currency ?? 'EUR');
                  })()}
                  chartConfig={btevChartConfig}
                  chartData={btevChartData}
                  className="bg-white rounded-[16px] p-[0.85rem] flex-1 md:border border-lightGray md:py-[1.75rem] md:flex md:items-center md:justify-center md:flex-col"
                  minHeight={isMobileView ? 140 : 152}
                  outerRadius={isMobileView ? 53 : 64}
                />

                <PieGraph
                  title="Achieved BTRT"
                  titleValue={(() => {
                    if (graphViewMode === 'percentage') {
                      const achievedItem = btrtChartData.find((item) => item.key === 'achieved');
                      return achievedItem ? `${achievedItem.value}%` : '0%';
                    } else {
                      // Euro mode - display the actual achieved value
                      const btrtAchieved = budget?.btrt_achieved || 0;
                      return `€${btrtAchieved.toLocaleString()}`;
                    }
                  })()}
                  chartConfig={btrtChartConfig}
                  chartData={btrtChartData}
                  variant="primary"
                  className="bg-white rounded-[16px] p-[0.85rem] flex-1 md:border border-lightGray md:py-[1.75rem] md:flex md:items-center md:justify-center md:flex-col"
                  minHeight={isMobileView ? 140 : 152}
                  outerRadius={isMobileView ? 53 : 64}
                />
              </div>
            </div>
          </div>
        </div>

        {/* GENERAL INFORMATION - MOBILE */}
        {isMobileView && (
          <BudgetGeneralInfoAccordion
            description={budget?.description}
            bmevThreshold={budget?.bmev_alert_threshold}
            btrtThreshold={budget?.btrt_alert_threshold}
            currency={budget?.currency}
            autoRenewal={budget?.is_auto_renewal}
            startDate={formatDate(budget?.start_date)}
            endDate={formatDate(budget?.end_date)}
            bmev={budget?.bmev + budget?.bmev_used}
            btrt={budget?.btrt}
            bhov={budget?.bhov ?? 0}
            btoh={budget?.btoh ?? 0}
            btev={budget?.btev ?? (budget?.bmev ?? 0) + (budget?.bmev_used ?? 0) + (budget?.bhov ?? 0)}
          />
        )}

        {/* BANK ACCOUNTS AND ACCOUNTING CENTERS */}
        <div className="xl:flex xl:items-stretch xl:justify-between xl:gap-6 xl:min-h-[300px]">
          {/* BANK ACCOUNTS */}
          <OverviewList
            title="Bank Accounts"
            items={bankAccounts}
            icon={vaultIcon}
            addButtonIcon={plusIcon}
            addButtonTitle="ADD BANK ACCOUNTS"
            valuePrefix={budget?.currency === 'EUR' ? '€' : '$'}
            valueSuffix="k"
            onAddClick={canCreateBankAccount ? handleAddBankClick : undefined}
            showAddButton={canCreateBankAccount}
            className="flex-1 mt-8 bg-lightPurple xl:w-[33%] rounded-[16px] md:flex-2 md:bg-white md:border md:border-lightGray"
            onSeeAllClick={handleSeeAllBankClick}
            onItemClick={(id) => handleBankAccountClick(id)}
          />

          {/* ACCOUNTING CENTERS */}
          <div className="flex-1 mt-8 bg-lightPurple xl:w-[33%] rounded-[16px] md:bg-white md:border md:border-lightGray">
            <div className="p-4 px-2 md:px-4 pb-2 md:pb-4">
              {/* HEADER */}
              <div className="flex justify-between mb-[0.85rem]">
                <p className="pl-2 text-xs text-lightPurple-300 font-medium md:text-foreground md:font-bold md:text-base">
                  Accounting Centers
                </p>
              </div>

              {/* ACCOUNTING CENTER ITEMS */}
              <div className="border border-white rounded-[8px] space-y-0 px-4 md:px-0 md:space-y-4">
                {loadingAccountingCenters ? (
                  <p className="text-secondary text-sm text-center py-6">Loading...</p>
                ) : (
                  ACCOUNTING_CENTERS.map((item) => {
                    // Find the accounting center in the fetched data
                    const acCenter = accountingCenters.find((ac: any) => ac.type === item.value && ac?.is_default);

                    return (
                      <AccountCenterToggleItem
                        key={item.value}
                        budgetId={budgetId}
                        budget={budget}
                        item={item}
                        acCenter={acCenter}
                        value={accountingCentersToggle[item.value]}
                        defaultChecked={acCenter?.is_default}
                        disabled={!canToggleAccountingCenterActivation}
                        showToggle={canToggleAccountingCenterActivation}
                        onChange={async (checked: boolean, setChecked: any) => {
                          if (!canToggleAccountingCenterActivation) {
                            return;
                          }

                          if (checked) {
                            // Trying to activate
                            const success = await handleActivateAC(item.value);
                            // The toggle will update via state if API succeeds
                          } else {
                            // Trying to deactivate
                            handleDeactivateAC(item.value);
                            // The toggle state won't change until confirmation
                          }
                        }}
                      />
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* EXPENSE AND REVENUE */}
          <div className="flex-1 mt-8 bg-lightPurple xl:w-[33%] rounded-[16px] md:bg-white md:border md:border-lightGray">
            <div className="p-4 px-2 md:px-4 pb-2 md:pb-4">
              {/* HEADER */}
              <div className="flex justify-between mb-[0.85rem]">
                <p className="pl-2 text-xs text-lightPurple-300 font-medium md:text-foreground md:font-bold md:text-base">
                  Expense & Revenue
                </p>
              </div>

              {/* EXPENSE AND REVENUE ITEMS */}
              <div className="border border-white rounded-[8px] space-y-0 px-4 md:px-0 md:space-y-4">
                <ToggleItem
                  key={EXPENSE_ITEM.value}
                  budgetId={budgetId}
                  item={EXPENSE_ITEM}
                  value={expenseToggle}
                  defaultChecked={false}
                  disabled={!canToggleExpenseCategoryActivation}
                  showToggle={canToggleExpenseCategoryActivation}
                  onChange={async (checked: boolean, setChecked: any) => {
                    if (!canToggleExpenseCategoryActivation) {
                      return;
                    }

                    if (checked) {
                      // Trying to activate
                      await handleActivateExpense();
                    } else {
                      // Trying to deactivate
                      await handleDeactivateExpense();
                    }
                  }}
                  onItemClick={() => {
                    // Navigate to Expense Categories page if toggle is active
                    if (expenseToggle && budgetId) {
                      navigate(`/budgets/${budgetId}/expense-categories`);
                    }
                  }}
                />
                <ToggleItem
                  key={REVENUE_ITEM.value}
                  budgetId={budgetId}
                  item={REVENUE_ITEM}
                  value={revenueToggle}
                  defaultChecked={false}
                  disabled={!canToggleRevenueCategoryActivation}
                  showToggle={canToggleRevenueCategoryActivation}
                  onChange={async (checked: boolean, setChecked: any) => {
                    if (!canToggleRevenueCategoryActivation) {
                      return;
                    }

                    if (checked) {
                      // Trying to activate
                      await handleActivateRevenue();
                    } else {
                      // Trying to deactivate
                      await handleDeactivateRevenue();
                    }
                  }}
                  onItemClick={() => {
                    // Add navigation or other click handling here if needed
                    if (revenueToggle && budgetId) {
                      navigate(`/budgets/${budgetId}/revenue-categories`);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* INVOICE AND COMMITMENTS */}
        <div
          className={`w-full min-w-0 md:flex md:flex-col xl:flex-row ${invoices.length > 0 ? 'md:items-stretch' : 'md:items-stretch'} md:gap-6 xl:gap-6`}
        >
          {/* INVOICE — min-w-0 + flex-1 so the table can shrink; table scrolls inside */}
          <section className="mt-7 min-w-0 flex-1 md:bg-white md:p-4 md:rounded-[16px] md:border md:border-lightGray md:flex md:flex-col">
            <div className="flex items-center justify-between">
              <h4 className="flex items-center gap-2">
                <img src={invoiceIcon} alt="invoice-icon" />
                <span className="font-bold">Recent Invoice</span>
              </h4>

              {invoices && invoices.length > 0 && (
                <Button
                  variant="text"
                  onClick={() => navigate(`/budgets/${budgetId}/invoices`)}
                  className="text-base font-semibold py-0 h-[30px]"
                >
                  See All
                </Button>
              )}
            </div>
            {invoices.length > 0 || activeInvoiceTab !== 'All' ? (
              <div className="flex flex-col gap-2 bg-lightPurple rounded-[16px] p-4 py-5 mt-4 md:mt-3 md:p-0 md:bg-white min-w-0">
                <TabPills
                  options={['All', 'Expense', 'Revenue']}
                  value={activeInvoiceTab}
                  onValueChange={setActiveInvoiceTab}
                />

                <div className="min-w-0 overflow-x-auto">
                  <InvoicesTable
                    onInvoiceSelect={handleInvoiceSelect}
                    invoices={invoices.slice(0, 10)}
                    setSelectedInvoice={() => {}}
                  />
                </div>
              </div>
            ) : (
              <div className="bg-lightPurple rounded-[16px] p-6 flex items-center justify-center flex-col gap-3 mt-4 md:bg-white md:flex-1">
                <div className="flex flex-col items-center justify-center md:flex-row md:gap-4">
                  <img src={noInvoiceIcon} alt="no invoice icon" className="md:w-[40px] md:h-[40px]" />
                  <span className="mt-[3px] text-xs font-medium text-center leading-[16.5px] md:text-left">
                    <span className="md:text-base md:font-medium md:block mb-[-3px] md:text-foreground">
                      There are no Invoice to show.
                    </span>{' '}
                    <span className="md:text-secondary md:text-base md:font-medium">
                      {' '}
                      Please create a new invoice to allocate budget!
                    </span>
                  </span>
                </div>
                {canCreateInvoice && (
                  <Button
                    onClick={() =>
                      navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
                        state: { from: 'budget', budgetId }
                      })
                    }
                    variant="primary-reverse"
                    className="rounded-full font-semibold px-5 text-base"
                  >
                    Create an Invoice
                  </Button>
                )}
              </div>
            )}
          </section>

          {/* COMMITMENTS — fixed max width so it is not squeezed off-screen */}
          <div className="mt-7 w-full md:max-w-[400px] shrink-0 xl:ml-0 md:bg-white md:p-4 md:rounded-[16px] md:border md:border-lightGray">
            <p className="text-base font-bold mb-2">Commitments</p>

            <div className="flex flex-col gap-4 md:gap-2 w-full min-w-0">
              <CommitmentCalendar
                commitments={budgetCommitments.map(commitmentCalendarRow)}
                density="narrow"
                selectedDate={date ?? null}
                onDateSelect={handleCommitmentDateSelect}
                currentMonth={calendarMonth}
                onMonthChange={setCalendarMonth}
                className="border rounded-[16px] p-2 py-3 md:p-0 md:border-0 md:rounded-none"
              />
              <div className="h-px bg-secondary w-full" />
              {date && (
                <CommitmentList
                  date={date}
                  commitments={selectedDateCommitments.length > 0 ? [selectedDateCommitments[0]] : []}
                  onCreateCommitment={canCreateInvoice ? handleCreateBudgetCommitment : undefined}
                  variant={isMobileView ? 'default' : 'secondary'}
                  loading={commitmentsLoading}
                  showOnDesktop
                  invoiceNavigateState={{
                    budget: budget as Budget | undefined,
                    from: 'budget',
                    budgetId: budgetId || undefined
                  }}
                  className="md:max-h-[300px] md:overflow-y-auto bg-transparent md:bg-white border-t-0 md:border-t-0 p-0 md:p-4"
                />
              )}
            </div>
          </div>

          <div className="rounded-[22px] flex items-center justify-center absolute bottom-[26px] right-[36px] shadow-[0px_4px_4px_0px_#00000040]">
            <Select key={budgetFabSelectKey} onValueChange={handleBudgetFabAction}>
              <SelectTrigger
                aria-label="Budget quick actions"
                className="px-3 flex items-center w-[64px] h-[64px] rounded-[22px] border-0 bg-primary text-white shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 data-[size=default]:h-[64px] justify-center [&>svg:last-child]:hidden"
              >
                {/* <SelectValue className="sr-only" placeholder="Actions" /> */}
                <InvoiceUploadIcon className="size-8 shrink-0 pointer-events-none" width={42} height={42} />
              </SelectTrigger>
              <SelectContent side="top" align="end" className="min-w-[220px] p-1">
                {canCreateInvoice && <SelectItem value="upload-invoice">Upload an Invoice</SelectItem>}
                {canCreateInvoice && <SelectSeparator />}
                {canCreateWorkActivity && <SelectItem value="log-work-activity">Log Work Activity</SelectItem>}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* WORK ACTIVITY */}
        <section className="mt-7 min-w-0 flex-1 md:min-h-[450px] md:bg-white md:p-4 md:rounded-[16px] md:border md:border-lightGray md:flex md:flex-col">
          <div className="flex items-center justify-between">
            <h4 className="flex items-center gap-2">
              <img src={workActivityIcon} alt="work-activity-icon" />
              <span className="font-bold">Work Activity</span>
            </h4>

            {totalWorkActivityCount > 7 && (
              <Button
                variant="text"
                onClick={() => navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity`)}
                className="text-base font-semibold py-0 h-[30px]"
              >
                See All
              </Button>
            )}
          </div>

          {workActivities.length > 0 || activeWorkActivityTab !== 'All' || loadingWorkActivities ? (
            <div className="flex flex-col gap-2 bg-lightPurple rounded-[16px] p-4 py-5 mt-4 md:mt-3 md:p-0 md:bg-white min-w-0 md:flex-1">
              <TabPills
                options={[...WORK_ACTIVITY_FILTER_OPTIONS]}
                value={activeWorkActivityTab}
                onValueChange={(value) =>
                  setActiveWorkActivityTab(value as (typeof WORK_ACTIVITY_FILTER_OPTIONS)[number])
                }
                badges={workActivityTabBadges}
              />

              <div className="min-w-0 overflow-x-auto">
                <WorkActivityTable
                  workActivities={filteredWorkActivities}
                  loading={loadingWorkActivities}
                  currency={budget?.currency}
                  budgetId={budgetId}
                  onWorkActivitySelect={handleWorkActivitySelect}
                />
              </div>
            </div>
          ) : (
            <div className="bg-lightPurple rounded-[16px] p-6 flex items-center justify-center flex-col gap-3 mt-4 md:bg-white md:flex-1">
              <div className="flex flex-col items-center justify-center md:flex-row md:gap-4">
                <img src={noWorkActivityIcon} alt="no work activity icon" className="md:w-[40px] md:h-[40px]" />
                <span className="mt-[3px] text-xs font-medium text-center leading-[16.5px] md:text-left">
                  <span className="md:text-base md:font-medium md:block mb-[-3px] md:text-foreground">
                    There are no work activity to show.
                  </span>{' '}
                  <span className="md:text-secondary md:text-base md:font-medium">
                    You can create work activity to request a payment
                  </span>
                </span>
              </div>
              {canCreateWorkActivity && (
                <Button
                  onClick={() =>
                    navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/create`, {
                      state: { from: 'budget', budgetId }
                    })
                  }
                  variant="primary-reverse"
                  className="rounded-full font-semibold px-5 text-sm"
                >
                  Create a Work Activity
                </Button>
              )}
            </div>
          )}
        </section>

        {/* Delete Confirm Modal */}
        {deleteConfirmModal && (
          <InfoModal
            isOpen={deleteConfirmModal}
            variant="confirm"
            title="Are you sure want to Delete this Budget?"
            message={<p>Deleted budget will removed permanently and unable to restore</p>}
            buttonText="Delete"
            onButtonClick={handleDeleteBudget}
            onClose={() => setDeleteConfirmModal(false)}
            icon={deleteBudgetIcon}
          />
        )}

        {/* Deactivate Expense Confirm Modal */}
        {deactivateExpenseConfirmModal && (
          <InfoModal
            isOpen={deactivateExpenseConfirmModal}
            variant="confirm"
            title="Are you sure want to Deactivate Expense?"
            message={<p>All Expense under this category will automatically deleted</p>}
            buttonText="Deactivate"
            onButtonClick={confirmDeactivateExpense}
            onClose={() => {
              setDeactivateExpenseConfirmModal(false);
            }}
            icon={toggleECOffIcon}
          />
        )}

        {/* Deactivate Revenue Confirm Modal */}
        {deactivateRevenueConfirmModal && (
          <InfoModal
            isOpen={deactivateRevenueConfirmModal}
            variant="confirm"
            title="Are you sure want to Deactivate Revenue?"
            message={<p>All Revenue under this category will automatically deleted</p>}
            buttonText="Deactivate"
            onButtonClick={confirmDeactivateRevenue}
            onClose={() => {
              setDeactivateRevenueConfirmModal(false);
            }}
            icon={toggleECOffIcon}
          />
        )}

        {/* Deactivate AC Confirm Modal */}
        {deactivateACConfirmModal && (
          <InfoModal
            isOpen={deactivateACConfirmModal}
            variant="confirm"
            title="Are you sure want to Deactivate Department Accounting Center Category?"
            message={<p>All Accounting Center under Department Category will automatically deleted</p>}
            buttonText="Deactivate"
            onButtonClick={confirmDeactivateAC}
            onClose={() => {
              setDeactivateACConfirmModal(false);
            }}
            icon={toggleACOffIcon}
          />
        )}

        {/* Explanatory Modal */}
        {isOpen && (
          <ExplanatoryModal
            isOpen={isOpen}
            steps={steps}
            currentStep={currentStep}
            buttonText="Next"
            onButtonClick={() => {
              setCurrentStep(currentStep + 1);
            }}
            onClose={() => {
              setIsOpen(false);
            }}
            onStepChange={setCurrentStep}
          />
        )}
      </div>
    </>
  );
};

export default DetailPage;
